/**
 * Строки модулей IT-работ в «Деталях» карточки: модуль, отчёт (Баку), согласующий, статус, перенос
 * одобрения из прошлого раунда, дата и комментарий решения; сверху «Согласовано N из M».
 * Колонками в дровер (440 px) и на телефон не уложить — каждая строка блоком, мета переносится.
 */
import { Text, XStack, YStack } from 'tamagui'

import { useApp, type Localized } from '../../../i18n'
import type { ItWorksLineState, ItWorksPayload, RequestCard } from '../../../lib/types'
import type { Tone } from '../../../theme/tones'
import { Tag } from '../../../ui'
import { fmtDateTime } from '../components/CardDetails'
import { itWorksNames, itWorksSummary } from './itWorksCard'

export const LINE_STATUS: Record<ItWorksLineState['status'], { label: Localized; tone: Tone }> = {
  pending: { label: { ru: 'Ожидает решения', en: 'Awaiting decision', az: 'Qərar gözlənilir' }, tone: 'gold' },
  approved: { label: { ru: 'Согласовано', en: 'Approved', az: 'Razılaşdırılıb' }, tone: 'green' },
  rework: { label: { ru: 'На доработку', en: 'Rework', az: 'Düzəlişə' }, tone: 'red' },
  excluded: { label: { ru: 'Исключена', en: 'Excluded', az: 'Çıxarılıb' }, tone: 'default' },
}
/** «за: ФИО» — решение принял заместитель на время отпуска. */
export const FOR_WHOM: Localized = { ru: 'за:', en: 'for:', az: 'əvəzinə:' }
const REPORT: Localized = { ru: 'Отчёт', en: 'Report', az: 'Hesabat' }

export function ItWorksModulesTable({ card }: { card: RequestCard }) {
  const { t } = useApp()
  const lines = card.itWorks?.lines ?? []
  if (!lines.length) return null
  const { approved, total } = itWorksSummary(lines)
  const names = itWorksNames(card)
  const name = (id?: string | null) => (id && names.get(id)) || '—'
  const p = card.payload as Partial<ItWorksPayload>
  const reports = p.reports ?? []
  // Баку: строка ссылается на отчёт — имя файла из вложений, иначе «Отчёт N» по порядку в заявке;
  // описание отчёта от инициатора — следом.
  const reportOf = (reportId?: string) => {
    if (p.direction !== 'BAKU') return null
    const i = reports.findIndex((r) => r.id === reportId)
    if (i < 0) return null
    const file = card.attachments?.find((a) => a.uploadId === reports[i].uploadId)?.upload.name
    const note = reports[i].note ? ` — ${reports[i].note}` : ''
    return `${file ? `${t(REPORT)}: ${file}` : `${t(REPORT)} ${i + 1}`}${note}`
  }

  return (
    <YStack gap={6}>
      <Text fontSize={13} fontWeight="600" color="$color">
        {t({ ru: 'Согласовано {n} из {m}', en: 'Approved {n} of {m}', az: '{m} sətirdən {n} razılaşdırılıb' })
          .replace('{n}', String(approved))
          .replace('{m}', String(total))}
      </Text>
      <YStack>
        {lines.map((line, i) => {
          const d = line.decision
          const st = LINE_STATUS[line.status]
          // Решённая строка — кто решал (заместитель — «за: ФИО»); нерешённая — назначенный согласующий.
          const who = d ? `${name(d.approverId)}${d.actingForId ? ` · ${t(FOR_WHOM)} ${name(d.actingForId)}` : ''}` : name(line.approverId)
          const row = p.modules?.find((m) => m.id === line.lineId)
          const meta = [
            reportOf(row?.reportId),
            `${t({ ru: 'Согласующий', en: 'Approver', az: 'Razılaşdıran' })}: ${who}`,
            row?.note ? `${t({ ru: 'Примечание', en: 'Note', az: 'Qeyd' })}: ${row.note}` : '',
          ]
          const when = [
            d ? fmtDateTime(d.decidedAt) : '',
            line.carried && line.decidedRound != null
              ? t({ ru: 'из раунда {n}', en: 'from round {n}', az: '{n}-ci raunddan' }).replace('{n}', String(line.decidedRound))
              : '',
          ]
          return (
            <YStack
              key={line.lineId}
              gap={3}
              paddingVertical={9}
              borderBottomWidth={i < lines.length - 1 ? 1 : 0}
              borderColor="$borderSecondary"
              opacity={line.status === 'excluded' ? 0.55 : 1}
            >
              <XStack alignItems="flex-start" justifyContent="space-between" gap={8}>
                <Text flexShrink={1} fontSize={13} fontWeight="600" color="$color">
                  {line.label ?? '—'}
                </Text>
                <Tag tone={st.tone}>{t(st.label)}</Tag>
              </XStack>
              <Text fontSize={12} color="$muted" lineHeight={17}>
                {meta.filter(Boolean).join(' · ')}
              </Text>
              {when.some(Boolean) ? (
                <Text fontSize={12} color="$muted">
                  {when.filter(Boolean).join(' · ')}
                </Text>
              ) : null}
              {d?.comment ? (
                <Text fontSize={12} color="$color" lineHeight={17}>
                  «{d.comment}»
                </Text>
              ) : null}
            </YStack>
          )
        })}
      </YStack>
    </YStack>
  )
}
